"use client";

import staticData from "@/public/data/hotel/results.json";
import { Pulse } from "@/shared/components/common/Animations";
import Box from "@/shared/components/common/Box";
import Flex from "@/shared/components/common/Flex";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import Typography from "@/shared/components/common/Typography";
import { cn } from "@/shared/libs/commonUtils";
import { isUndefined } from "@/shared/libs/type-utils";
import type { Accommodation } from "@/types/services/hotel/apis/Accommodations";
import type { Hotel } from "@/types/services/hotel/apis/Availability";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { memo, useCallback, useMemo, useState } from "react";
import SpecialOfferText from "../SpecialOfferText";
import HotelCardBadge from "./HotelCardBadge";
import HotelCardCarousel from "./HotelCardCarousel";
import HotelCardLocationToggle from "./HotelCardLocationToggle";
import HotelCardPanel from "./HotelCardPanel";
import HotelCardPriceAndReserve from "./HotelCardPriceAndReserve";
import HotelCardSummary from "./HotelCardSummary";
import HotelCardTags from "./HotelCardTags";

const HotelCardMap = dynamic(() => import("./HotelCardMap"), {
  ssr: false,
  loading: () => (
    <Flex>
      <Pulse />
    </Flex>
  ),
});

type ToggleState = "open" | "close";
export type PanelState = keyof PanelsState;
type PanelsState = {
  map: ToggleState;
};

interface HotelCardProps {
  hotel: Hotel | Accommodation;
  searchParams: SearchParams;
  deviceType: DeviceType;
}

export default memo(function HotelCard({
  hotel,
  searchParams,
  deviceType,
}: HotelCardProps) {
  const { discountLabel, locationLabel, bookedLabel, specialOfferLabel } =
    staticData.body.resultCard;
  const [togglePanel, setTogglePanel] = useState<PanelsState>({
    map: "close",
  });

  const {
    name,
    address,
    stars,
    images,
    mapLat,
    mapLong,
    min_price,
    total_price,
    slug,
    tags,
    offer,
  } = hotel;
  const { checkIn, checkOut, rooms, hasStay } = searchParams;

  const isPanelOpen = Object.values(togglePanel).includes("open");
  const isBooked = isUndefined(min_price) && isUndefined(total_price);
  const pathName = `${usePathname().split("/").slice(1, 3).join("/")}/${slug}`;

  const nextPageUrl = `/${pathName}/${hasStay === "true" ? `?checkIn=${checkIn}&checkOut=${checkOut}&rooms=${rooms}` : ""}`;

  const discountPercentage = useMemo(
    () =>
      !isUndefined(min_price) &&
      !isUndefined(total_price) &&
      total_price !== min_price
        ? Math.trunc(((total_price - min_price) / total_price) * 100)
        : 0,
    [min_price, total_price],
  );

  const handleTogglePanel = useCallback(
    (panel: PanelState) => (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setTogglePanel((prev) => ({
        ...prev,
        [panel]: prev[panel] === "open" ? "close" : "open",
      }));
    },
    [],
  );

  const handleImageGalleryClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const HotelCard = (
    <Flex
      direction={"col"}
      className="ring-1 ring-border rounded-lg bg-background shadow-1 md:shadow-0 hover:shadow-2 transition duration-75 ease-linear select-none relative animate-fade-in"
    >
      {/*TODO: Replace with a better UI*/}
      {offer && (
        <Flex className="w-full justify-end items-center absolute left-[15%]">
          <SpecialOfferText />
        </Flex>
      )}

      <Flex
        className={cn(
          "w-full rounded-t-md md:rounded-t-lg p-3",
          isPanelOpen ? "ring-1 ring-border" : "",
        )}
        direction={{ default: "col", md: "row" }}
      >
        <Flex.Item
          className="w-full md:basis-3/12 relative"
          alignSelf={"center"}
        >
          {discountPercentage > 0 && (
            <HotelCardBadge position="right">
              <PhosphorIcon
                name="SealPercent"
                size={18}
                weight="fill"
                className="text-tertiary"
              />
              <Flex gap="1">
                <Typography
                  variant="caption"
                  fontWeight="semibold"
                  className="text-foreground"
                >
                  {discountPercentage}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="normal"
                  className="text-foreground"
                >
                  {discountLabel}
                </Typography>
              </Flex>
            </HotelCardBadge>
          )}

          {isBooked && (
            <HotelCardBadge position="right">
              <PhosphorIcon
                name="Empty"
                size={18}
                weight="bold"
                className="text-destructive/80"
              />
              <Typography
                variant="caption"
                fontWeight="normal"
                className="text-foreground"
              >
                {bookedLabel}
              </Typography>
            </HotelCardBadge>
          )}

          {offer && (
            <HotelCardBadge position="left" className="bg-background md:hidden">
              <PhosphorIcon
                name="Fire"
                size={19}
                weight="fill"
                className="text-secondary"
              />
              <Typography
                variant="caption"
                fontWeight="semibold"
                className="text-foreground"
              >
                {specialOfferLabel}
              </Typography>
            </HotelCardBadge>
          )}

          <Box onClick={handleImageGalleryClick}>
            <HotelCardCarousel
              images={images}
              slug={hotel.slug}
              deviceType={deviceType}
            />
          </Box>
        </Flex.Item>

        <Flex.Item className="md:basis-9/12 p-1 md:pr-4 max-md:pt-4">
          <Flex
            direction={"col"}
            justifyContent={"between"}
            className="h-full"
            gap={"3"}
          >
            <HotelCardSummary name={name} address={address} stars={+stars} />

            <Flex
              justifyContent={"between"}
              alignItems={"end"}
              className="max-md:items-center max-md:justify-center"
            >
              <Flex.Item className="hidden md:flex">
                <Flex alignItems={"center"} gap={"1"}>
                  <Flex.Item className="hidden md:flex">
                    <HotelCardLocationToggle
                      toggleMap={togglePanel.map}
                      handleToggleMap={handleTogglePanel}
                      locationLabel={locationLabel}
                    />
                  </Flex.Item>
                  <Flex.Item className="hidden md:flex">
                    {!isUndefined(tags) && tags?.length > 0 && (
                      <Flex.Item className="hidden md:flex">
                        <HotelCardTags tags={tags} />
                      </Flex.Item>
                    )}
                  </Flex.Item>
                </Flex>
              </Flex.Item>

              <HotelCardPriceAndReserve
                hotel={hotel}
                searchParams={searchParams}
                nextPageUrl={nextPageUrl}
              />
            </Flex>
          </Flex>
        </Flex.Item>
      </Flex>

      <HotelCardPanel isOpen={isPanelOpen}>
        {togglePanel.map === "open" && (
          <HotelCardMap mapLat={+mapLat} mapLong={+mapLong} />
        )}
      </HotelCardPanel>
    </Flex>
  );

  return deviceType === "mobile" ? (
    <Link href={nextPageUrl}>{HotelCard}</Link>
  ) : (
    HotelCard
  );
});
